import type { Passage } from "./types";

export const passage: Passage = {
  slug: "1-corinthians-13",
  reference: "1 Corinthians 13:4–7",
  title: "1 Corinthians 13: the love chapter is not a wedding reading",
  h1: "1 Corinthians 13, and the argument it was written into",
  description:
    "Love is patient, love is kind is read at weddings. It sits between two chapters about spiritual gifts, addressed to a church using them to rank each other.",
  targets: [
    "1 corinthians 13 meaning",
    "love is patient love is kind explained",
    "1 corinthians 13 context",
  ],
  published: "2026-09-03",
  standfirst:
    "Nothing in the chapter is about marriage. It is an intervention in a dispute, and knowing that makes every line in it sharper.",
  verse: {
    text: "Love is patient and is kind. Love doesn't envy. Love doesn't brag, is not proud, doesn't behave itself inappropriately, doesn't seek its own way, is not provoked, takes no account of evil; doesn't rejoice in unrighteousness, but rejoices with the truth; bears all things, believes all things, hopes all things, and endures all things.",
    ref: "1 Corinthians 13:4–7",
  },
  context: [
    {
      text: "If I speak with the languages of men and of angels, but don't have love, I have become sounding brass or a clanging cymbal. If I have the gift of prophecy, and know all mysteries and all knowledge, and if I have all faith, so as to remove mountains, but don't have love, I am nothing.",
      ref: "1 Corinthians 13:1–3",
    },
    {
      text: "But now faith, hope, and love remain—these three. The greatest of these is love.",
      ref: "1 Corinthians 13:13",
    },
  ],
  takeaways: [
    "Chapters 12 and 14 are both about spiritual gifts and how a divided church was using them. Chapter 13 is the middle of that argument, not a break from it.",
    "The list in verses 4 to 7 maps almost item for item onto the behaviour Paul has been correcting in the letter: envy, boasting, pride, self-seeking, irritability.",
    "Read at a wedding it describes a feeling. Read in place it describes conduct between people who are annoying each other, which is harder and more useful.",
    "The chapter never mentions marriage, romance or a couple. Its subject is a congregation.",
  ],
  body: [
    {
      type: "h2",
      id: "what-surrounds-it",
      text: "What surrounds it",
    },
    {
      type: "p",
      text: "Chapter 12 is about the body having many parts and no part being able to tell another it is unnecessary. Chapter 14 is about the practical management of gifts in a meeting — who speaks, in what order, and why intelligibility matters more than impressiveness.",
    },
    {
      type: "p",
      text: "Chapter 13 sits between them and opens by dismissing the most impressive gifts in the strongest terms available: tongues without love is noise, prophecy and knowledge and mountain-moving faith without love is nothing. It is an intervention in an argument about status.",
    },
    {
      type: "h2",
      id: "the-list-is-a-rebuke",
      text: "The list is a rebuke",
    },
    {
      type: "p",
      text: "Set the qualities of love against the problems named earlier in the letter and they line up. The Corinthians were envious, boastful, arrogant, litigious, self-seeking at the shared meal, and quick to take offence. Verses 4 to 7 are that list, negated.",
    },
    {
      type: "p",
      text: "Which is why the passage feels bracing rather than sentimental when it is read in place. It is not a description of a feeling anybody has. It is a description of behaviour a specific church was not managing.",
    },
    {
      type: "p",
      text: "The opening three verses make the same point by escalation, and they are worth reading slowly because each one raises the stakes. Tongues of men and angels. Prophecy, all mysteries, all knowledge, mountain-moving faith. Giving away everything, and then the body itself. At every step the verdict is the same, and by the third it has become uncomfortable: a person can give away everything they own and be, by this measure, nothing at all.",
    },
    {
      type: "h2",
      id: "the-famous-ending",
      text: "The famous ending",
    },
    {
      type: "p",
      text: "Faith, hope and love remain, and the greatest is love. In context the argument is about permanence: the gifts being competed over are temporary — prophecy will pass, tongues will cease, knowledge will pass away — and the thing that outlasts them is the thing nobody was showing off about.",
    },
    {
      type: "p",
      text: "The mirror image in verse 12 belongs to the same point. Seeing in part now, fully later, is an argument for humility about what anybody currently knows.",
    },
  ],
  misreading: {
    claim:
      "That it is a description of romantic love — the definitive biblical statement about marriage, and therefore the natural reading at a wedding.",
    correction: [
      "The chapter has been read at weddings for a long time and there is nothing wrong with using it there; the qualities described are exactly the ones a marriage runs on. But taken as its subject, the wedding reading empties it. It becomes a description of how two people feel about each other at their most hopeful, which is the moment least in need of it.",
      "In place, it is addressed to people who were suing each other, humiliating the poor at the shared meal, and ranking one another by which gift they had. Patient, kind, not envious, not boastful, not irritable, keeps no record — every item is a correction of something happening in that room.",
      "Which makes it a considerably more demanding passage. Love here is not what you feel at the altar; it is what you do about the person in the congregation whose gift you resent, and Paul says that without it the most impressive spiritual credentials amount to nothing at all.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Read chapters 12 to 14 in one sitting once. It takes about ten minutes and it permanently changes what chapter 13 sounds like.",
    },
    {
      type: "p",
      text: "Then apply it where it was aimed: the person you are ranking yourself against. That is the same territory as {envy and comparison}, and this chapter is the strongest thing in the New Testament on it.",
      links: { "envy and comparison": "/verses/bible-verses-about-comparison" },
    },
    {
      type: "note",
      text: "One clause repays more attention than the rest: takes no account of evil, which older translations render as keeps no record of wrongs. It is an accounting term. In a congregation where people were suing each other and remembering slights, the instruction is not to feel differently about what happened but to stop maintaining the ledger — which is a thing a person can actually decide to do.",
    },
    {
      type: "note",
      text: "A useful exercise, and an uncomfortable one: read verses 4 to 7 substituting your own name for the word love, and notice which clause stops you.",
    },
  ],
  faq: [
    {
      q: "What is 1 Corinthians 13 actually about?",
      a: "Spiritual gifts and status in a divided church. It sits between two chapters on the subject, and its list of what love is maps onto the specific behaviour Paul has been correcting — envy, boasting, arrogance, self-seeking and irritability.",
    },
    {
      q: "Is 1 Corinthians 13 about marriage?",
      a: "It never mentions marriage or romance. It is addressed to a congregation, and the qualities it lists are about conduct between people who are annoying each other — which is why it also works at a wedding, though that is not its subject.",
    },
    {
      q: "What does the greatest of these is love mean?",
      a: "In context it is an argument about permanence: the gifts being competed over will pass away, and love will not. It closes a chapter aimed at people ranking each other by which gift they had.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so all four verses are printed rather than the first line alone. It reads 'takes no account of evil' where others have 'keeps no record of wrongs' — the same clause, and the one most often left off the wedding card.",
    },
  ],
  related: ["social-media-fast-guide", "phone-rules-for-christian-families"],
  relatedTopics: [
    "bible-verses-about-comparison",
    "bible-verses-about-forgiveness",
  ],
  relatedPassages: ["romans-12-2", "philippians-4-13"],
};
